import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Users } from 'lucide-react';

const CURRENT_YEAR = new Date().getFullYear();

const formatCurrency = (value) => {
  if (Math.abs(value) >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div style={{
      background: 'var(--surface-secondary)',
      border: '1px solid var(--border-primary)',
      borderRadius: '0.5rem',
      padding: '0.875rem',
      boxShadow: 'var(--shadow-elevation-2)',
      minWidth: '200px'
    }}>
      <div style={{
        fontWeight: 600,
        color: 'var(--text-primary)',
        marginBottom: '0.5rem',
        fontSize: '0.875rem'
      }}>
        {data.fullName || label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', fontSize: '0.8125rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Projects:</span>
          <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{data.projectCount}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Revenue:</span>
          <span style={{ color: 'var(--interactive-primary)', fontWeight: 500 }}>
            {formatCurrency(data.revenue)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Labor Cost:</span>
          <span style={{ color: 'var(--status-warning)', fontWeight: 500 }}>
            {formatCurrency(data.laborCost)}
          </span>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem',
          borderTop: '1px solid var(--border-primary)',
          paddingTop: '0.375rem',
          marginTop: '0.25rem'
        }}>
          <span style={{ color: 'var(--text-secondary)' }}>Profit:</span>
          <span style={{
            color: data.profit >= 0 ? 'var(--status-success)' : 'var(--status-error)',
            fontWeight: 600
          }}>
            {formatCurrency(data.profit)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Avg Margin:</span>
          <span style={{
            color: data.avgMarginPercent >= 50 ? 'var(--status-success)' :
                   data.avgMarginPercent >= 20 ? 'var(--status-warning)' : 'var(--status-error)',
            fontWeight: 500
          }}>
            {data.avgMarginPercent.toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
};

const ClientProfitChart = ({ data, allProjects, chartColors }) => {
  const [topN, setTopN] = useState(15);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);

  // Get available years from project data
  const availableYears = useMemo(() => {
    const years = new Set();
    if (allProjects) {
      allProjects.forEach(project => {
        if (project.initialAcceptance) {
          years.add(new Date(project.initialAcceptance).getFullYear());
        }
      });
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [allProjects]);

  // Filter data based on selected year by recalculating from projects
  const filteredData = useMemo(() => {
    if (!allProjects || !selectedYear) return data;

    // Filter projects to selected year
    const yearProjects = allProjects.filter(p => {
      if (!p.initialAcceptance) return false;
      const year = new Date(p.initialAcceptance).getFullYear();
      return year === selectedYear;
    });

    // Recalculate client profitability for filtered projects
    const clientStats = {};
    for (const project of yearProjects) {
      if (!project.clientId || !project.clientName) continue;

      if (!clientStats[project.clientId]) {
        clientStats[project.clientId] = {
          clientId: project.clientId,
          clientName: project.clientName,
          projectCount: 0,
          revenue: 0,
          laborCost: 0,
          profit: 0,
          marginSum: 0
        };
      }

      clientStats[project.clientId].projectCount += 1;
      clientStats[project.clientId].revenue += project.revenue;
      clientStats[project.clientId].laborCost += project.laborCost;
      clientStats[project.clientId].profit += project.profit;
      clientStats[project.clientId].marginSum += project.marginPercent;
    }

    return Object.values(clientStats)
      .map(c => ({
        clientId: c.clientId,
        clientName: c.clientName,
        projectCount: c.projectCount,
        revenue: Math.round(c.revenue * 100) / 100,
        laborCost: Math.round(c.laborCost * 100) / 100,
        profit: Math.round(c.profit * 100) / 100,
        avgMarginPercent: c.projectCount > 0
          ? Math.round((c.marginSum / c.projectCount) * 10) / 10
          : 0
      }))
      .sort((a, b) => b.profit - a.profit);
  }, [allProjects, selectedYear, data]);

  // Sort by profit descending and take top N
  const chartData = [...filteredData]
    .sort((a, b) => b.profit - a.profit)
    .slice(0, topN)
    .map(item => ({
      ...item,
      fullName: item.clientName,
      name: item.clientName.length > 20
        ? item.clientName.substring(0, 17) + '...'
        : item.clientName
    }));

  if (filteredData.length === 0) {
    return (
      <div className="chart-section">
        <div className="chart-header">
          <div>
            <h3 className="chart-title">Profitability by Client</h3>
            <p className="chart-subtitle">Top clients ranked by total profit ({selectedYear})</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Year:</span>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  background: 'var(--surface-tertiary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '0.375rem',
                  color: 'var(--text-primary)',
                  cursor: 'pointer'
                }}
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="empty-state">
          <Users size={48} />
          <h3>No client data available for {selectedYear}</h3>
          <p>No projects with labor cost data found for this year.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-section">
      <div className="chart-header">
        <div>
          <h3 className="chart-title">Profitability by Client</h3>
          <p className="chart-subtitle">Top clients ranked by total profit ({selectedYear})</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Year:</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.875rem',
                background: 'var(--surface-tertiary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '0.375rem',
                color: 'var(--text-primary)',
                cursor: 'pointer'
              }}
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Show:</span>
            <select
              value={topN}
              onChange={(e) => setTopN(parseInt(e.target.value))}
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.875rem',
                background: 'var(--surface-tertiary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '0.375rem',
                color: 'var(--text-primary)',
                cursor: 'pointer'
              }}
            >
              <option value={10}>Top 10</option>
              <option value={15}>Top 15</option>
              <option value={20}>Top 20</option>
              <option value={25}>Top 25</option>
            </select>
          </div>
        </div>
      </div>

      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={chartColors.borderPrimary}
              horizontal={true}
              vertical={false}
            />
            <XAxis
              type="number"
              tickFormatter={formatCurrency}
              tick={{ fill: chartColors.textSecondary, fontSize: 12 }}
              axisLine={{ stroke: chartColors.borderPrimary }}
              tickLine={{ stroke: chartColors.borderPrimary }}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: chartColors.textPrimary, fontSize: 12 }}
              axisLine={{ stroke: chartColors.borderPrimary }}
              tickLine={false}
              width={140}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: '1rem' }}
              formatter={(value) => (
                <span style={{ color: chartColors.textSecondary, fontSize: '0.875rem' }}>
                  {value}
                </span>
              )}
            />
            <Bar
              dataKey="revenue"
              name="Revenue"
              fill="#3B82F6"
              radius={[0, 4, 4, 0]}
            />
            <Bar
              dataKey="laborCost"
              name="Labor Cost"
              fill="#F59E0B"
              radius={[0, 4, 4, 0]}
            />
            <Bar
              dataKey="profit"
              name="Profit"
              fill="#10B981"
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Table */}
      <div style={{ marginTop: '1.5rem', overflowX: 'auto' }}>
        <table className="profit-table" style={{ fontSize: '0.8125rem' }}>
          <thead>
            <tr>
              <th>Client</th>
              <th style={{ textAlign: 'right' }}>Projects</th>
              <th style={{ textAlign: 'right' }}>Revenue</th>
              <th style={{ textAlign: 'right' }}>Labor Cost</th>
              <th style={{ textAlign: 'right' }}>Profit</th>
              <th style={{ textAlign: 'right' }}>Margin</th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((client) => (
              <tr key={client.clientId}>
                <td style={{ fontWeight: 500 }}>{client.fullName}</td>
                <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                  {client.projectCount}
                </td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                  {formatCurrency(client.revenue)}
                </td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', color: 'var(--status-warning)' }}>
                  {formatCurrency(client.laborCost)}
                </td>
                <td style={{
                  textAlign: 'right',
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  color: client.profit >= 0 ? 'var(--status-success)' : 'var(--status-error)'
                }}>
                  {formatCurrency(client.profit)}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span className={`margin-badge ${
                    client.avgMarginPercent >= 50 ? 'high' :
                    client.avgMarginPercent >= 20 ? 'medium' : 'low'
                  }`}>
                    {client.avgMarginPercent.toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientProfitChart;
